/**
 * Konstanten + Mapper der BK-Anfragen-Seite (Betreuungskräfte-Pipeline).
 *
 * Eigenständiges Pendant zu src/pages/anfragen/anfragenConstants.ts — bewusst
 * NICHT aus dem PB-Modul umgebaut, sondern als neues Modul angelegt. Nur
 * exportierte Helfer aus lib/v1 werden wiederverwendet.
 */
import { primaryProfile } from '../../lib/v1';
import type { V1User } from '../../lib/v1';

/** UI-Zeile der BK-Anfragen-Liste (Board-Karte + Tabellenzeile). */
export interface BkAnfrage {
  /** "BK-{user_id}" — eindeutige UI-ID. */
  id: string;
  /** Voller Name der Betreuungskraft (Vorname + Nachname). */
  name: string;
  /** Kontaktzeile (E-Mail oder Telefon). */
  contact: string;
  phone: string;
  email: string;
  city: string;
  /** Relativer Eingangszeitpunkt — i18n-Key + optionaler Zähler, wird erst
   *  beim Rendern (BkAnfragenPage) mit der aktuellen UI-Sprache aufgelöst. */
  received: RelativeTimeSpec;
  /** Admin-ID des zugewiesenen Bearbeiters (null = nicht zugewiesen). */
  assigned_to?: number | null;
  /** Anzeigename des zugewiesenen Bearbeiters ('' = keiner). */
  assigned_to_name?: string;
  /** Wiedervorlage-Spiegel (users.wiedervorlage_am, YYYY-MM-DD) + Notiz. */
  wiedervorlage_am?: string | null;
  wiedervorlage_notiz?: string | null;
}

// Server-seitige Pagination: die BK-Liste hat mehrere Tausend Einträge.
export const BK_ANFRAGEN_PER_PAGE = 50;

// Tabellen-Spalte → serverseitiges Sortierfeld (getUsersByRole-Konvention:
// `profile: true` = Spalte auf der jointen profiles-Tabelle).
export const BK_ANFRAGEN_SORT_FIELDS: Record<string, { field: string; profile?: boolean }> = {
  id:       { field: 'id' },
  name:     { field: 'nachname', profile: true },
  city:     { field: 'ort', profile: true },
  status:   { field: 'konto' },
  received: { field: 'created_at' },
  // Wie bei den PB-Anfragen: echte users.wiedervorlage_am-Spalte.
  wiedervorlage: { field: 'wiedervorlage_am' },
};

// Reihenfolge + i18n-Key der Tabellen-Header (sortKey verweist in SORT_FIELDS).
export const BK_ANFRAGEN_TABLE_HEADERS: { key: string; sortKey?: string }[] = [
  { key: 'id', sortKey: 'id' },
  { key: 'name', sortKey: 'name' },
  { key: 'city', sortKey: 'city' },
  { key: 'status', sortKey: 'status' },
  { key: 'received', sortKey: 'received' },
  { key: 'wiedervorlage', sortKey: 'wiedervorlage' },
  { key: 'assigned_to' },
];

/**
 * Relatives Datums-Label ("vor 2 Std", "gestern", "3 Tage") — sprach-
 * abhängig, daher NICHT hier vorformatiert, sondern über eine i18n-
 * `TFunction` zur Render-Zeit (siehe `bkRelativeTimeKey` + Aufrufer in
 * BkAnfragenPage.tsx). So wechselt das Label mit der UI-Sprache, ohne dass
 * die Liste neu geladen werden muss.
 */
export interface RelativeTimeSpec {
  key: string;
  count?: number;
}

export function bkRelativeTimeKey(iso: string): RelativeTimeSpec {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return { key: 'anfragen_bk.relative_time.just_now' };
  if (min < 60) return { key: 'anfragen_bk.relative_time.minutes', count: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { key: 'anfragen_bk.relative_time.hours', count: h };
  const d = Math.floor(h / 24);
  if (d === 1) return { key: 'anfragen_bk.relative_time.yesterday' };
  if (d < 7) return { key: 'anfragen_bk.relative_time.days', count: d };
  const w = Math.floor(d / 7);
  return { key: 'anfragen_bk.relative_time.weeks', count: w };
}

/**
 * Mappt einen rohen BK-User (user_role=1) auf die UI-Zeile der
 * BK-Anfragen-Seite. Anders als beim PB-Pendant (v1UserToAnfrage) wird der
 * volle Name (Vorname + Nachname) angezeigt — BK sind Einzelpersonen, keine
 * Familienkonten.
 */
export function v1UserToBkAnfrage(u: V1User): BkAnfrage {
  const p = primaryProfile(u);
  const name = [p?.vorname, p?.nachname].filter(Boolean).join(' ')
    || [u.vorname, u.nachname].filter(Boolean).join(' ')
    || u.email
    || `BK #${u.id}`;
  return {
    id: `BK-${u.id}`,
    name,
    contact: u.email ?? p?.telefon ?? '—',
    phone: u.mobile ?? p?.telefon ?? '',
    email: u.email ?? '',
    city: p?.ort ?? '',
    received: bkRelativeTimeKey(u.created_at),
    assigned_to: typeof u.assigned_to === 'number' ? u.assigned_to : (u.assigned_to ?? null),
    assigned_to_name: typeof u.assigned_admin_name === 'string' ? u.assigned_admin_name : '',
    wiedervorlage_am: u.wiedervorlage_am ?? null,
    wiedervorlage_notiz: u.wiedervorlage_notiz ?? null,
  };
}
